import { Plan } from "../types";

/**
 * Promotional code definitions.
 *
 * This mirrors the backend config (backend/config/promotions.php). The backend
 * is the source of truth and re-validates on submit — this client copy exists
 * only to give the customer instant feedback and a live discounted price.
 */
export interface PromoDefinition {
  code: string;
  label: string;
  discountAmount: number; // dollars off per month
  durationMonths: number;
  excludedPlanIds: string[];
}

export const PROMOS: Record<string, PromoDefinition> = {
  CRISP30: {
    code: "CRISP30",
    label: "$30 off per month for 6 months",
    discountAmount: 30,
    durationMonths: 6,
    // The $59 CRISP Wireless plan (CRISP_47) is excluded.
    excludedPlanIds: ["CRISP_47"],
  },
};

export interface AppliedPromo {
  code: string;
  label: string;
  discountAmount: number;
  durationMonths: number;
  originalPrice?: number;
  discountedPrice?: number;
}

export type PromoValidation =
  | { ok: true; promo: AppliedPromo }
  | { ok: false; error: string };

/**
 * Validate a promo code against the selected plan and compute the discounted
 * price. Returns a discriminated result so the UI can show success or an error.
 */
export function validatePromo(
  rawCode: string,
  plan: Plan | null
): PromoValidation {
  const normalized = (rawCode || "").trim().toUpperCase();

  if (!normalized) {
    return { ok: false, error: "Please enter a promo code." };
  }

  const def = PROMOS[normalized];
  if (!def) {
    return { ok: false, error: "That promo code isn't valid." };
  }

  if (plan && def.excludedPlanIds.includes(plan.plan_id)) {
    return {
      ok: false,
      error: `${def.code} can't be used on the ${plan.name} plan.`,
    };
  }

  const promo: AppliedPromo = {
    code: def.code,
    label: def.label,
    discountAmount: def.discountAmount,
    durationMonths: def.durationMonths,
  };

  if (plan && typeof plan.price === "number" && plan.price > 0) {
    promo.originalPrice = plan.price;
    promo.discountedPrice = Math.max(plan.price - def.discountAmount, 0);
  }

  return { ok: true, promo };
}
